// Výkaz práce — filtr období podle kalendářních dnů v Europe/Prague (stejný záměr jako materiály / deník).

import type { ObjectLiteral, SelectQueryBuilder } from 'typeorm';

export const WORK_REPORT_DATE_FILTER_TIMEZONE = 'Europe/Prague';

/** Platné ISO datum `YYYY-MM-DD`. */
export function isIsoDateOnly(value: string | undefined | null): value is string {
  const text = (value ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text);
}

/** Obě hranice období jsou platné ISO datum (seskupení pod klíč `range`). */
export function hasCompleteWorkReportDateRange(
  dateFrom: string | undefined | null,
  dateTo: string | undefined | null,
): boolean {
  return isIsoDateOnly(dateFrom) && isIsoDateOnly(dateTo);
}

/** Alespoň jedna hranice období je platná. */
export function hasWorkReportDateBounds(
  dateFrom: string | undefined | null,
  dateTo: string | undefined | null,
): boolean {
  return isIsoDateOnly(dateFrom) || isIsoDateOnly(dateTo);
}

/**
 * Inclusive filtr `created_at` podle kalendářního dne v Europe/Prague.
 * `dateFrom` / `dateTo` se aplikují nezávisle (stačí jedna hranice).
 */
export function applyWorkReportCreatedAtDateFilter<Entity extends ObjectLiteral>(
  qb: SelectQueryBuilder<Entity>,
  alias: string,
  options?: { dateFrom?: string; dateTo?: string },
  paramPrefix = 'wrDate',
): void {
  const dateFrom = options?.dateFrom?.trim();
  const dateTo = options?.dateTo?.trim();
  const tz = WORK_REPORT_DATE_FILTER_TIMEZONE;
  if (isIsoDateOnly(dateFrom)) {
    qb.andWhere(
      `(${alias}.created_at AT TIME ZONE '${tz}')::date >= CAST(:${paramPrefix}From AS date)`,
      { [`${paramPrefix}From`]: dateFrom },
    );
  }
  if (isIsoDateOnly(dateTo)) {
    qb.andWhere(
      `(${alias}.created_at AT TIME ZONE '${tz}')::date <= CAST(:${paramPrefix}To AS date)`,
      { [`${paramPrefix}To`]: dateTo },
    );
  }
}

/** Minimální povolené datum řádku výkazu (UTC). */
export const WORK_REPORT_ENTRY_CREATED_AT_MIN_MS = Date.parse('2015-01-01T00:00:00.000Z');

/** Kolik ms do budoucnosti smí admin posunout razítko (24 h). */
export const WORK_REPORT_ENTRY_CREATED_AT_MAX_FUTURE_MS = 24 * 60 * 60 * 1000;

/** `datetime-local` hodnota z ISO / Date (lokální čas prohlížeče). */
export function workReportCreatedAtToDatetimeLocalInput(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Parsuje ISO nebo `datetime-local` na Date. */
export function parseWorkReportEntryCreatedAtInput(
  value: string | Date | undefined | null,
): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  const text = value.trim();
  if (!text) return null;
  const date = new Date(text);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** Validace razítka výkazu — vrací chybovou hlášku nebo null. */
export function validateWorkReportEntryCreatedAt(
  date: Date,
  now: Date = new Date(),
): string | null {
  if (!Number.isFinite(date.getTime())) {
    return 'Neplatné datum a čas výkazu.';
  }
  if (date.getTime() < WORK_REPORT_ENTRY_CREATED_AT_MIN_MS) {
    return 'Datum výkazu je příliš daleko v minulosti.';
  }
  if (date.getTime() > now.getTime() + WORK_REPORT_ENTRY_CREATED_AT_MAX_FUTURE_MS) {
    return 'Datum výkazu nemůže být v budoucnosti.';
  }
  return null;
}

/** Měsíční klíč `YYYY-MM` pro seskupení řádků (kalendář Prague). */
export function workReportPragueMonthKey(createdAt: Date | string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: WORK_REPORT_DATE_FILTER_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date(createdAt));
  const year = parts.find((p) => p.type === 'year')?.value ?? '0000';
  const month = parts.find((p) => p.type === 'month')?.value ?? '00';
  return `${year}-${month}`;
}
